import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Download, CheckCircle } from 'lucide-react';
import { EventCards } from '@/components/holiday/EventCards';

const HolidayThankYou = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || 'your email';

  const handleDownload = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backendUrl}/api/v1/holiday/download`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>You're In! | KGOB Holiday Kit</title>
        <meta name="description" content="Your KGOB Holiday Money-Saver Kit is on the way. Check your inbox." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <Header />
      
      <main className="py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          {/* Success Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-semibold text-[#0B2240] mb-4">
              You're In! 🎉
            </h1>
            
            <p className="text-xl text-[#4C5A70] mb-8">
              Your entry for the $1,000 Visa® Holiday Gift Card Giveaway has been received.
            </p>
            
            <div className="max-w-2xl mx-auto text-left bg-white rounded-2xl p-8 shadow-lg mb-8">
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                Here's what happens next:
              </h2>
              <ul className="space-y-4 text-[#4C5A70]">
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] text-xl">📧</span>
                  <span><strong>Watch your inbox</strong> — we'll send your 1-Page Holiday Money-Saver Kit within a few minutes.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] text-xl">🎁</span>
                  <span><strong>On December 12, 2025</strong>, we'll draw one winner at random for the $1,000 Visa® gift card and notify them by email.</span>
                </li>
              </ul>
            </div>
            
            <div className="max-w-2xl mx-auto bg-gradient-to-br from-[#FFF9E6] to-[#FFFEF8] rounded-2xl p-8 border-2 border-[#D4AF37]/20 mb-12">
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-3">
                Want to go deeper right now?
              </h3>
              <p className="text-[#4C5A70] mb-6">
                Use our 2025 tax estimator tool at <strong className="text-[#D4AF37]">LetsTalkTaxes.io</strong> to see how upcoming tax changes may impact your refund — before the busy season hits.
              </p>
              <Button data-testid="holiday-thank-you-button-1"
                asChild
                className="bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] hover:brightness-95 h-12 px-8 font-semibold"
              >
                <a data-testid="holiday-thank-you-a-2" href="https://letstalktaxes.io" target="_blank" rel="noopener noreferrer">
                  Try the 2025 Tax Estimator →
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mb-16"></div>

          {/* Events Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0B2240] text-center mb-4">
              Next Steps
            </h2>
            <p className="text-lg text-[#4C5A70] text-center mb-10">
              You'll get first dibs on these upcoming events:
            </p>
            
            <EventCards />

            <p className="text-sm text-[#9CA3AF] text-center mt-10">
              <a data-testid="holiday-thank-you-a-1" href="#" className="text-[#D4AF37] hover:underline">
                Add a reminder to your calendar (December)
              </a>
            </p>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default HolidayThankYou;
